import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getAppLanguage, setAppLanguage, getTextField, getToggleLanguages, t } from '../i18n/translations'

const TITLE_MAP = {
  amharic: 'ሥርዓተ ቅዳሴ - Sirate Kidase',
  tigrinya: 'ሥርዓተ ቅዳሴ - Sirate Kidase',
  latin: 'Sirate Kidase Tutor'
}

const ICON_MAP = {
  amharic: '/icons/amharic.webp',
  tigrinya: '/icons/tigrinya.png',
  latin: '/icons/latin.png'
}

const VISIBILITY_KEY = 'sk_text_visibility'
const DEFAULT_VISIBILITY = { geez: true, middle: true, english: true }

function readVisibility() {
  try {
    const saved = JSON.parse(localStorage.getItem(VISIBILITY_KEY))
    if (!saved) return DEFAULT_VISIBILITY
    const merged = { ...DEFAULT_VISIBILITY, ...saved }
    // Never restore a state where every text is hidden
    if (!merged.geez && !merged.middle && !merged.english) return DEFAULT_VISIBILITY
    return merged
  } catch {
    return DEFAULT_VISIBILITY
  }
}

const LanguageContext = createContext()

export function LanguageProvider({ children }) {
  const [appLanguage, setAppLang] = useState(getAppLanguage)
  // Text toggle: native vs latin within the current app language
  const [activeKey, setActiveKey] = useState(appLanguage)

  const translationField = activeKey === 'latin' ? '_latintext' : getTextField()
  const toggleLanguages = getToggleLanguages()

  // Per-script visibility of the lesson text versions (applies to every lesson card)
  const [textVisibility, setTextVisibility] = useState(readVisibility)

  const toggleTextVisibility = useCallback((key) => {
    setTextVisibility(prev => {
      const next = { ...prev, [key]: !prev[key] }
      // Keep at least one text version visible
      if (!next.geez && !next.middle && !next.english) return prev
      try {
        localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next))
      } catch {
        // Storage unavailable (private mode) — visibility stays session-only
      }
      return next
    })
  }, [])

  // Labels for the "Show text" control, derived from the startup app language
  const textVersions = [
    { key: 'geez', label: t('categoryGeez') },
    { key: 'middle', label: activeKey === 'latin' ? t('langToggleLatin') : t('langToggleNative') },
    { key: 'english', label: t('langLatin') },
  ]

  // Change the entire app language
  function changeAppLanguage(lang) {
    setAppLanguage(lang)
    setAppLang(lang)
    setActiveKey(lang) // reset text toggle to new native language
  }

  // Update document title and favicon when app language changes
  useEffect(() => {
    document.title = TITLE_MAP[appLanguage] || TITLE_MAP.latin
    const iconEl = document.querySelector('link[rel="icon"]')
    const appleIconEl = document.querySelector('link[rel="apple-touch-icon"]')
    const iconHref = ICON_MAP[appLanguage] || ICON_MAP.latin
    if (iconEl) iconEl.href = iconHref
    if (appleIconEl) appleIconEl.href = iconHref
  }, [appLanguage])

  return (
    <LanguageContext.Provider value={{
      language: activeKey,
      setLanguage: setActiveKey,
      translationField,
      toggleLanguages,
      appLanguage,
      changeAppLanguage,
      textVisibility,
      toggleTextVisibility,
      textVersions,
      t,
    }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
